import React, { useState } from "react";
import { createCustomerCompany } from "../../../../services/staffingService";
import { useParams, useHistory } from "react-router-dom";
import { useProviderCompany } from "../../../../context/ProviderCompanyContext";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowLeft, Building2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DashboardSidebar from "../agencyDashboardSidebar";
import ProgressSteps from "./jobform/ProgressSteps";
import Step1CompanyDetails from "./steps/Step1CompanyDetails";
import Step2BusinessContact from "./steps/Step2BusinessContact";
import Step3ReviewSubmit from "./steps/Step3ReviewSubmit";

const schema = yup
  .object({
    companyLegalName: yup.string().required("Company Legal Name is required."),
    companyFriendlyName: yup.string().required("Friendly Name is required."),
    contactPersonName: yup.string().required("Contact Person is required."),
    contactEmail: yup
      .string()
      .email("Must be a valid email")
      .required("Email is required."),
    hourlyPayRate: yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .required("Hourly billing rate is required.")
      .positive("Hourly billing rate must be positive.")
      .min(45, "Hourly billing rate must be at least $45")
      .max(500, "Hourly billing rate cannot exceed $500"),
    companyWebsiteUrl: yup.string(),
    facilityType: yup.string().required("Facility Type is required."),
  })
  .required();

const STEP_FIELDS = {
  1: ["companyLegalName", "companyFriendlyName", "facilityType"],
  2: ["hourlyPayRate", "companyWebsiteUrl", "contactPersonName", "contactEmail"],
};

const STEP_TITLES = ["Company Details", "Business & Contact", "Documents & Review"];
const TOTAL_STEPS = 3;

const NewCustomer = () => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { facilityType: "" },
  });

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [documentFile, setDocumentFile] = useState(null);
  const [showSideDrawer, setShowSideDrawer] = useState(false);
  const [err, setErr] = useState("");
  const [errType, setErrType] = useState("");

  const history = useHistory();
  const { providerCompanyId } = useParams();

  const goToNextStep = async () => {
    const fields = STEP_FIELDS[currentStep];
    if (fields) {
      const isValid = await trigger(fields);
      if (!isValid) return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const goToPreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("companyLegalName", data.companyLegalName);
      formData.append("companyFriendlyName", data.companyFriendlyName);
      formData.append("hourlyPayRate", data.hourlyPayRate);
      formData.append("companyWebsiteUrl", data.companyWebsiteUrl);
      formData.append("contactPersonName", data.contactPersonName);
      formData.append("contactEmail", data.contactEmail);
      formData.append("customerContact", data.customerContact);
      formData.append("facilityType", data.facilityType);
      if (documentFile) formData.append("documentFile", documentFile);
      await createCustomerCompany(providerCompanyId, formData);
      history.push(`/provider-company/${providerCompanyId}/crm`);
    } catch (ex) {
      setLoading(false);
      if (ex.response && ex.response.status === 404) {
        setErr(ex.response.data);
        setErrType("advance");
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1CompanyDetails register={register} errors={errors} />;
      case 2:
        return (
          <Step2BusinessContact
            register={register}
            errors={errors}
            setValue={setValue}
            control={control}
          />
        );
      case 3:
        return (
          <Step3ReviewSubmit
            watch={watch}
            setDocumentFile={setDocumentFile}
            err={err}
            errType={errType}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-inter">
      <DashboardSidebar isOpen={showSideDrawer} setIsOpen={setShowSideDrawer} />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">

          {/* Back button */}
          <button
            onClick={() => history.push(`/provider-company/${providerCompanyId}/crm`)}
            className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Customers
          </button>

          {/* Page header */}
          <div className="mb-6 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building2 size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">New Customer</h1>
                <p className="text-sm text-gray-500">Add a new customer to your CRM</p>
              </div>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">

              {/* Progress steps */}
              <div className="p-6 pb-0">
                <ProgressSteps
                  currentStep={currentStep}
                  totalSteps={TOTAL_STEPS}
                  stepTitles={STEP_TITLES}
                />
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Step content */}
                <div className="p-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      {renderStep()}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center px-6 pb-6 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    disabled={currentStep === 1}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      currentStep === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Previous
                  </button>

                  {currentStep < TOTAL_STEPS ? (
                    <button
                      type="button"
                      onClick={goToNextStep}
                      className="px-5 py-2 text-white rounded-md text-sm font-medium transition-colors"
                      style={{ backgroundColor: "var(--primary)" }}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-5 py-2 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ backgroundColor: "var(--primary)" }}
                    >
                      {loading ? "Creating..." : "Add Customer"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  );
};

export default NewCustomer;
